import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { getModels } from './api'
import type { SparkyModelKey, SparkyModelOption } from './types'

/**
 * Two-option segmented control for the model of the next answer (spec-78).
 * Labels and the Opus hint come from `GET /sparky/models`: no model names live
 * in the frontend. Controlled: the parent decides what a change means (keep it
 * locally for a draft chat, PATCH for an existing one).
 */
export function ModelSwitch({
  value,
  onChange,
  disabled = false,
}: {
  value: SparkyModelKey
  onChange: (key: SparkyModelKey) => void
  disabled?: boolean
}) {
  const [options, setOptions] = useState<SparkyModelOption[]>([])

  useEffect(() => {
    let cancelled = false
    getModels()
      .then((models) => {
        if (!cancelled) setOptions(models)
      })
      .catch(() => {
        // Without the list the switch stays hidden; Sparky keeps its default model.
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (options.length === 0) return null
  const selectedHint = options.find((option) => option.key === value)?.hint ?? null

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="radiogroup" aria-label="Model" className="inline-flex rounded-lg border border-border p-0.5">
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={option.key === value}
            title={option.hint ?? undefined}
            disabled={disabled}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs transition-colors disabled:opacity-60 max-md:min-h-9',
              option.key === value
                ? 'bg-muted font-medium text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
            onClick={() => {
              if (option.key !== value) onChange(option.key)
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
      {selectedHint ? <span className="text-xs text-muted-foreground">{selectedHint}</span> : null}
    </div>
  )
}
